import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { ArrowRight, BookOpen, Download, TrendingUp, Shield, Users, Sparkles, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [featuredDownloads, setFeaturedDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [coursesRes, downloadsRes] = await Promise.all([
          axios.get(`${API}/courses?featured=true`),
          axios.get(`${API}/downloads?featured=true`)
        ]);
        setFeaturedCourses(coursesRes.data.slice(0, 3));
        setFeaturedDownloads(downloadsRes.data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: BookOpen,
      title: 'Expert-Led Courses',
      description: 'Learn from industry professionals with years of financial experience.'
    },
    {
      icon: Download,
      title: 'Digital Resources',
      description: 'Access templates, workbooks, and tools to accelerate your financial journey.'
    },
    {
      icon: TrendingUp,
      title: 'Practical Skills',
      description: 'Gain actionable knowledge you can apply immediately to your finances.'
    },
    {
      icon: Shield,
      title: 'Lifetime Access',
      description: 'Once purchased, access your content forever with all future updates.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 subtle-glow pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slideUp">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A202C] leading-tight">
                Master Your
                <span className="gradient-text block">Financial Future</span>
              </h1>
              <p className="mt-6 text-lg text-[#64748B] max-w-lg">
                Empowering consumers and businesses with the knowledge and tools to make smarter financial decisions. Start your journey to financial clarity today.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/courses">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all btn-hover-lift" data-testid="hero-explore-courses">
                    Explore Courses
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/downloads">
                  <Button variant="outline" className="border-[#2D4A5E] text-[#2D4A5E] hover:bg-[#F1F5F9] rounded-full px-8 py-6 text-lg font-medium" data-testid="hero-browse-resources">
                    Browse Resources
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fadeIn">
              <div className="aspect-video relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="https://customer-assets.emergentagent.com/job_financial-growth-hub/artifacts/rb5nav95_67391CCD-69DB-438B-A06D-8D36A8E4156F.mp4"
                  controls
                  poster="https://images.unsplash.com/photo-1758518726609-c551f858cd5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  className="w-full h-full object-cover"
                  data-testid="intro-video"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Featured SBA Content Banner */}
      <section className="py-8 bg-gradient-to-r from-[#1A202C] via-[#2D4A5E] to-[#1A202C] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex w-14 h-14 rounded-full bg-[#D4AF37] items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#1A202C]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-[#D4AF37] text-[#1A202C] text-xs font-bold rounded-full uppercase tracking-wide">New</span>
                  <span className="text-[#D4AF37] text-sm font-medium">Just Released</span>
                </div>
                <h3 className="text-white text-xl lg:text-2xl font-bold">Beyond the 7(a): Secure Up to $1M in SBA Loans</h3>
                <p className="text-white/70 text-sm mt-1 hidden sm:block">Stop settling for $350k. Learn the specialized loan programs most business owners never hear about.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <Link to="/product/course_sba_001" className="w-full sm:w-auto">
                <Button className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A202C] rounded-full px-6 py-5 font-semibold shadow-lg" data-testid="sba-course-btn">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Get the Course - $197
                </Button>
              </Link>
              <Link to="/product/download_sba_001" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 rounded-full px-6 py-5" data-testid="sba-cheatsheet-btn">
                  Cheat Sheet - $27
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Why Choose Capital Clarity?</h2>
            <p className="mt-4 text-[#64748B] max-w-2xl mx-auto">
              We provide comprehensive financial education designed to transform your relationship with money.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-[#F8F9FA] p-8 rounded-2xl border border-transparent hover:border-[#D4AF37]/20 transition-all card-hover">
                <div className="w-12 h-12 rounded-xl bg-[#2D4A5E] flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#1A202C] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#64748B]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Featured Courses</h2>
              <p className="mt-2 text-[#64748B]">Start your financial education journey</p>
            </div>
            <Link to="/courses">
              <Button variant="outline" className="hidden sm:flex items-center gap-2" data-testid="view-all-courses">
                View All Courses
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-[#F1F5F9] animate-pulse rounded-xl h-[400px]"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCourses.map(course => (
                <ProductCard key={course.product_id} product={course} />
              ))}
            </div>
          )}
          
          <Link to="/courses" className="sm:hidden">
            <Button className="w-full mt-6 bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full">
              View All Courses
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Downloads */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Digital Resources</h2>
              <p className="mt-2 text-[#64748B]">Tools and templates for immediate impact</p>
            </div>
            <Link to="/downloads">
              <Button variant="outline" className="hidden sm:flex items-center gap-2" data-testid="view-all-downloads">
                View All Resources
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-[#F1F5F9] animate-pulse rounded-xl h-[350px]"></div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredDownloads.map(download => (
                <ProductCard key={download.product_id} product={download} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-16 h-16 mx-auto mb-6 text-[#D4AF37]" />
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ready to Start Your Financial Journey?</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            Take the first step toward financial clarity with our expert-led courses and practical resources.
          </p>
          <Link to="/courses">
            <Button className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A202C] rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all btn-hover-lift" data-testid="cta-explore-courses">
              Explore Our Courses
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
